import { motion, useReducedMotion } from "framer-motion";
import { Reveal } from "./Reveal";

const MYTHS = [
  {
    myth: "I have to wait on insurance.",
    truth:
      "You don't. Most policies actually require you to prevent further damage — that's exactly what mitigation is. Waiting on an adjuster while water sits is how a small loss becomes a big one. Your claim processes in parallel; it doesn't need to happen first.",
  },
  {
    myth: "I need quotes before anyone touches anything.",
    truth:
      "Emergency mitigation is time-sensitive stabilization, not a renovation you shop around. The priority in the first 24–48 hours is extraction and drying. The photos we ask for are so our crew arrives with the right equipment — not to build an estimate.",
  },
  {
    myth: "My insurer said the crew has to come from them.",
    truth:
      "You have the right to choose your own restoration contractor, and the claim is treated the same either way. The adjuster works for the carrier. A preferred vendor works for the carrier's cost and volume targets. The contractor you choose is the only party working for you.",
  },
];

const MythCard = ({ myth, truth, index }) => {
  const reduce = useReducedMotion();
  const mythAnim = reduce
    ? {}
    : {
        initial: { opacity: 0, y: 14 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-15% 0px -15% 0px" },
        transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
      };
  const truthAnim = reduce
    ? {}
    : {
        initial: { opacity: 0.12, y: 12, filter: "blur(9px)" },
        whileInView: { opacity: 1, y: 0, filter: "blur(0px)" },
        viewport: { once: true, margin: "-15% 0px -15% 0px" },
        transition: { duration: 0.8, delay: 0.5, ease: [0.22, 1, 0.36, 1] },
      };

  return (
    <article className="rounded-2xl border border-white/10 bg-erg-deep/60 backdrop-blur-md p-6 sm:p-9">
      {/* Myth: submerged — settles first, muted and struck-through */}
      <motion.p
        {...mythAnim}
        data-testid={`myth-${index}`}
        className="font-display font-medium text-2xl sm:text-3xl leading-tight text-white/35 line-through decoration-white/30 decoration-2 blur-[0.5px] select-none"
      >
        “{myth}”
      </motion.p>
      {/* Truth: surfaces a beat later — sharpens and brightens into focus */}
      <div className="mt-5 flex gap-4">
        <motion.span
          aria-hidden="true"
          initial={reduce ? undefined : { scaleX: 0 }}
          whileInView={reduce ? undefined : { scaleX: 1 }}
          viewport={{ once: true, margin: "-15% 0px -15% 0px" }}
          transition={{ duration: 0.5, delay: 0.5, ease: "easeOut" }}
          className="mt-2 h-px w-8 shrink-0 origin-left bg-erg-aqua sm:w-12"
        />
        <motion.p
          {...truthAnim}
          data-testid={`truth-${index}`}
          className="text-white text-lg sm:text-xl leading-relaxed text-pretty"
        >
          {truth}
        </motion.p>
      </div>
    </article>
  );
};

export const MythBusters = () => {
  return (
    <section data-testid="myths-section" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="max-w-2xl">
          <Reveal>
            <span className="font-mono text-[12px] tracking-[0.22em] uppercase text-erg-aqua">
              Clearing the water
            </span>
          </Reveal>
          <Reveal delay={0.12}>
            <h2 className="mt-4 font-display font-bold uppercase text-white leading-[0.95] text-[clamp(2rem,6vw,3.75rem)]">
              Three things you were told<br className="hidden sm:block" /> that aren't true
            </h2>
          </Reveal>
        </div>

        <div className="mt-14 space-y-6">
          {MYTHS.map((m, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <MythCard myth={m.myth} truth={m.truth} index={i} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
